import {
  AirplaneTilt,
  ArrowRight,
  Baby,
  CalendarBlank,
  ChartBar,
  Check,
  Clock,
  CloudSun,
  Compass,
  CurrencyCircleDollar,
  GlobeHemisphereWest,
  IdentificationCard,
  Leaf,
  MapPin,
  Minus,
  Mountains,
  PersonSimpleWalk,
  Plus,
  ShieldCheck,
  Snowflake,
  Sparkle,
  Sun,
  UserCircle,
  Wallet,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, CSSProperties, FC, KeyboardEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import {
  BUDGET_IMAGES,
  CUSTOM_TIME_IMAGE,
  DURATION_IMAGES,
  SEASON_IMAGES,
  STYLE_IMAGES,
  TRAVELLER_IMAGES,
  VISA_IMAGES,
  WALKER_ATLAS,
  WALKER_FRAMES,
} from '@/app/agent/media.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { str } from '@/app/lib/a2ui/props.ts';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { BOARD_CONTOUR_FIELD, BOARD_CONTOURS } from './board-contours.ts';

/**
 * Client half of `TravelPassport`.
 *
 * This is the manual branch's game board rather than a static form. The visitor
 * makes five visual picks, then stamps a small demo travel document with the
 * details that let us match places: name, demo identity values, departure city
 * and gender. Every choice still writes to the contract's published paths, so
 * surprise mode can arrive fully seeded and the existing `submitPassport`
 * journey stays intact.
 */

const messages = defineMessages({
  builderHint: {
    id: 'travelPassport.builderHint',
    defaultMessage: 'Five quick picks. One trip that fits the way you want to travel.',
    description: 'Supporting line for the interactive travel preference game',
  },
  round: {
    id: 'travelPassport.round',
    defaultMessage: 'Round {current} of {total}',
    description: 'Progress label inside the travel preference game',
  },
  tripSignal: {
    id: 'travelPassport.tripSignal',
    defaultMessage: 'Your trip signal',
    description: 'Heading for the live preference summary',
  },
  tripSignalHint: {
    id: 'travelPassport.tripSignalHint',
    defaultMessage: 'Change any pick before you see your places.',
    description: 'Supporting line for the live preference summary',
  },
  toChoose: {
    id: 'travelPassport.toChoose',
    defaultMessage: 'Choose next',
    description: 'Placeholder for an unanswered preference in the summary',
  },
  identityRound: {
    id: 'travelPassport.identityRound',
    defaultMessage: 'Traveller',
    description: 'Short progress-rail label for the final identity round',
  },
  identityPrompt: {
    id: 'travelPassport.identityPrompt',
    defaultMessage: 'Stamp your traveller card',
    description: 'Heading for the final identity step',
  },
  identityHint: {
    id: 'travelPassport.identityHint',
    defaultMessage:
      'Use made-up demo details — nothing here is verified or used as a real passport.',
    description: 'Supporting line for the final identity step',
  },
  firstName: {
    id: 'travelPassport.firstName',
    defaultMessage: 'First name',
    description: 'Label for the given-name field',
  },
  firstNamePlaceholder: {
    id: 'travelPassport.firstNamePlaceholder',
    defaultMessage: 'Amelia…',
    description: 'Example given name',
  },
  surname: {
    id: 'travelPassport.surname',
    defaultMessage: 'Surname',
    description: 'Label for the family-name field',
  },
  surnamePlaceholder: {
    id: 'travelPassport.surnamePlaceholder',
    defaultMessage: 'Hart…',
    description: 'Example family name',
  },
  passportNumber: {
    id: 'travelPassport.passportNumber',
    defaultMessage: 'Passport number (demo)',
    description: 'Label for the made-up passport number field',
  },
  passportNumberPlaceholder: {
    id: 'travelPassport.passportNumberPlaceholder',
    defaultMessage: '1234567…',
    description: 'Example made-up passport number',
  },
  passportNumberHint: {
    id: 'travelPassport.passportNumberHint',
    defaultMessage: 'Any number works — please do not use a real passport number.',
    description: 'Supporting line for the demo passport number field',
  },
  dateOfBirth: {
    id: 'travelPassport.dateOfBirth',
    defaultMessage: 'Date of birth',
    description: 'Label for the demo date-of-birth field',
  },
  dateOfBirthHint: {
    id: 'travelPassport.dateOfBirthHint',
    defaultMessage: 'Demo date only — no identity check is performed.',
    description: 'Supporting line for the demo date-of-birth field',
  },
  origin: {
    id: 'travelPassport.origin',
    defaultMessage: 'Flying from',
    description: 'Label for the departure city field',
  },
  originPlaceholder: {
    id: 'travelPassport.originPlaceholder',
    defaultMessage: 'London…',
    description: 'Example departure city',
  },
  gender: {
    id: 'travelPassport.gender',
    defaultMessage: 'How should we address you?',
    description: 'Label for the traveller gender choice',
  },
  genderFemale: { id: 'travelPassport.genderFemale', defaultMessage: 'Female' },
  genderMale: { id: 'travelPassport.genderMale', defaultMessage: 'Male' },
  children: {
    id: 'travelPassport.children',
    defaultMessage: 'Children along',
    description: 'Label for the number-of-children stepper',
  },
  childrenSome: {
    id: 'travelPassport.childrenSome',
    defaultMessage: 'We will favour family-friendly places',
    description: 'Supporting line under the stepper when at least one child travels',
  },
  childrenFewer: {
    id: 'travelPassport.childrenFewer',
    defaultMessage: 'One child fewer',
    description: 'Accessible name for the stepper decrease button',
  },
  childrenMore: {
    id: 'travelPassport.childrenMore',
    defaultMessage: 'One child more',
    description: 'Accessible name for the stepper increase button',
  },
  styleRound: {
    id: 'travelPassport.styleRound',
    defaultMessage: 'The mood',
    description: 'Small title for the travel style round',
  },
  stylePrompt: {
    id: 'travelPassport.stylePrompt',
    defaultMessage: 'What should this trip feel like?',
    description: 'Question for the travel style round',
  },
  styleHint: {
    id: 'travelPassport.styleHint',
    defaultMessage: 'Pick the pull that gets you packing.',
    description: 'Supporting line for the travel style round',
  },
  styleSea: { id: 'travelPassport.styleSea', defaultMessage: 'Sea' },
  styleSeaDetail: {
    id: 'travelPassport.styleSeaDetail',
    defaultMessage: 'Beaches, islands and swimming weather',
  },
  styleExtreme: { id: 'travelPassport.styleExtreme', defaultMessage: 'Extreme places' },
  styleExtremeDetail: {
    id: 'travelPassport.styleExtremeDetail',
    defaultMessage: 'Mountains, deserts and wild landscapes',
  },
  styleCivilisation: { id: 'travelPassport.styleCivilisation', defaultMessage: 'Urban places' },
  styleCivilisationDetail: {
    id: 'travelPassport.styleCivilisationDetail',
    defaultMessage: 'Big cities, food, art and late nights',
  },
  styleGreenery: { id: 'travelPassport.styleGreenery', defaultMessage: 'Green places' },
  styleGreeneryDetail: {
    id: 'travelPassport.styleGreeneryDetail',
    defaultMessage: 'Forests, lakes and quiet trails',
  },
  styleBusiness: { id: 'travelPassport.styleBusiness', defaultMessage: 'Business trip' },
  styleBusinessDetail: {
    id: 'travelPassport.styleBusinessDetail',
    defaultMessage: 'Meetings, good wifi and a central hotel',
  },
  weatherRound: {
    id: 'travelPassport.weatherRound',
    defaultMessage: 'The forecast',
    description: 'Small title for the preferred weather round',
  },
  weatherPrompt: {
    id: 'travelPassport.weatherPrompt',
    defaultMessage: 'Choose your kind of weather',
    description: 'Question for the preferred weather round',
  },
  weatherHint: {
    id: 'travelPassport.weatherHint',
    defaultMessage: 'We’ll use it to find the season where your trip feels most like itself.',
    description: 'Supporting line for the preferred weather round',
  },
  weatherSummer: { id: 'travelPassport.weatherSummer', defaultMessage: 'Summer' },
  weatherSummerDetail: {
    id: 'travelPassport.weatherSummerDetail',
    defaultMessage: 'Long bright days and warm evenings',
  },
  weatherSpring: { id: 'travelPassport.weatherSpring', defaultMessage: 'Spring' },
  weatherSpringDetail: {
    id: 'travelPassport.weatherSpringDetail',
    defaultMessage: 'Mild air, new leaves and fewer crowds',
  },
  weatherAutumn: { id: 'travelPassport.weatherAutumn', defaultMessage: 'Autumn' },
  weatherAutumnDetail: {
    id: 'travelPassport.weatherAutumnDetail',
    defaultMessage: 'Crisp walks, warm light and good tables',
  },
  weatherWinter: { id: 'travelPassport.weatherWinter', defaultMessage: 'Winter' },
  weatherWinterDetail: {
    id: 'travelPassport.weatherWinterDetail',
    defaultMessage: 'Snow, clear skies and a quieter world',
  },
  visaRound: {
    id: 'travelPassport.visaRound',
    defaultMessage: 'Visa',
    description: 'Small title for the visa round',
  },
  visaPrompt: {
    id: 'travelPassport.visaPrompt',
    defaultMessage: 'Do you already hold a travel visa?',
    description: 'Question for the visa round',
  },
  visaHint: {
    id: 'travelPassport.visaHint',
    defaultMessage:
      'Some countries need a visa before you can enter. This tells us which ones belong on your shortlist.',
    description: 'Supporting line for the visa round',
  },
  visaYes: { id: 'travelPassport.visaYes', defaultMessage: 'I have a visa' },
  visaYesDetail: {
    id: 'travelPassport.visaYesDetail',
    defaultMessage: 'Include places that ask for one',
  },
  visaNo: { id: 'travelPassport.visaNo', defaultMessage: 'No visa yet' },
  visaNoDetail: {
    id: 'travelPassport.visaNoDetail',
    defaultMessage: 'Lean towards visa-free countries',
  },
  visaUnsure: { id: 'travelPassport.visaUnsure', defaultMessage: 'Not sure' },
  visaUnsureDetail: {
    id: 'travelPassport.visaUnsureDetail',
    defaultMessage: 'We flag the entry rules to check',
  },
  durationRound: {
    id: 'travelPassport.durationRound',
    defaultMessage: 'The window',
    description: 'Small title for the trip duration round',
  },
  durationPrompt: {
    id: 'travelPassport.durationPrompt',
    defaultMessage: 'How long can you disappear?',
    description: 'Question for the trip duration round',
  },
  durationHint: {
    id: 'travelPassport.durationHint',
    defaultMessage: 'A short reset and a slow wander are very different trips.',
    description: 'Supporting line for the trip duration round',
  },
  durationLongWeekend: { id: 'travelPassport.durationLongWeekend', defaultMessage: 'Long weekend' },
  durationLongWeekendDetail: {
    id: 'travelPassport.durationLongWeekendDetail',
    defaultMessage: 'Enough for one good neighbourhood',
  },
  durationWeek: { id: 'travelPassport.durationWeek', defaultMessage: 'A proper week' },
  durationWeekDetail: {
    id: 'travelPassport.durationWeekDetail',
    defaultMessage: 'The sweet spot for a first look',
  },
  durationTen: { id: 'travelPassport.durationTen', defaultMessage: 'Ten days' },
  durationTenDetail: {
    id: 'travelPassport.durationTenDetail',
    defaultMessage: 'Time to add a second rhythm',
  },
  durationFortnight: { id: 'travelPassport.durationFortnight', defaultMessage: 'Two weeks+' },
  durationFortnightDetail: {
    id: 'travelPassport.durationFortnightDetail',
    defaultMessage: 'Take the scenic route',
  },
  durationCustom: {
    id: 'travelPassport.durationCustom',
    defaultMessage: 'Custom time',
    description: 'Title for the custom number-of-days card',
  },
  durationDays: {
    id: 'travelPassport.durationDays',
    defaultMessage: 'days',
    description: 'Unit shown beside the custom duration stepper',
  },
  durationCustomFewer: {
    id: 'travelPassport.durationCustomFewer',
    defaultMessage: 'One day fewer',
    description: 'Accessible name for decreasing the custom duration',
  },
  durationCustomMore: {
    id: 'travelPassport.durationCustomMore',
    defaultMessage: 'One day more',
    description: 'Accessible name for increasing the custom duration',
  },
  budgetRound: {
    id: 'travelPassport.budgetRound',
    defaultMessage: 'Budget',
    description: 'Small title for the budget round',
  },
  budgetPrompt: {
    id: 'travelPassport.budgetPrompt',
    defaultMessage: 'What’s your comfortable range?',
    description: 'Question for the budget round',
  },
  budgetHint: {
    id: 'travelPassport.budgetHint',
    defaultMessage: 'A planning signal, not a live quote. We’ll match the shape of the trip to it.',
    description: 'Supporting line for the budget round',
  },
  budgetSimple: { id: 'travelPassport.budgetSimple', defaultMessage: 'Smart & simple' },
  budgetSimpleDetail: {
    id: 'travelPassport.budgetSimpleDetail',
    defaultMessage: 'Keep the good bits, skip the gloss',
  },
  budgetComfortable: { id: 'travelPassport.budgetComfortable', defaultMessage: 'Comfortable' },
  budgetComfortableDetail: {
    id: 'travelPassport.budgetComfortableDetail',
    defaultMessage: 'Room for a few lovely upgrades',
  },
  budgetTreat: { id: 'travelPassport.budgetTreat', defaultMessage: 'Treat yourself' },
  budgetTreatDetail: {
    id: 'travelPassport.budgetTreatDetail',
    defaultMessage: 'Make the stay part of the story',
  },
  budgetBig: { id: 'travelPassport.budgetBig', defaultMessage: 'Big swing' },
  budgetBigDetail: {
    id: 'travelPassport.budgetBigDetail',
    defaultMessage: 'More room for rare and remote',
  },
  budgetCustom: {
    id: 'travelPassport.budgetCustom',
    defaultMessage: 'Custom budget',
    description: 'Title for the custom amount card',
  },
  budgetCustomUnit: {
    id: 'travelPassport.budgetCustomUnit',
    defaultMessage: '$',
    description: 'Currency unit shown beside the custom budget stepper',
  },
  budgetCustomFewer: {
    id: 'travelPassport.budgetCustomFewer',
    defaultMessage: 'One hundred dollars fewer',
    description: 'Accessible name for decreasing the custom budget',
  },
  budgetCustomMore: {
    id: 'travelPassport.budgetCustomMore',
    defaultMessage: 'One hundred dollars more',
    description: 'Accessible name for increasing the custom budget',
  },
  back: {
    id: 'travelPassport.back',
    defaultMessage: 'Back',
    description: 'Button returning to the previous game round',
  },
  next: {
    id: 'travelPassport.next',
    defaultMessage: 'Lock it in',
    description: 'Button advancing to the next game round',
  },
  showPlaces: {
    id: 'travelPassport.showPlaces',
    defaultMessage: 'Show me my places',
    description: 'Button submitting the travel preferences',
  },

  missingStyle: {
    id: 'travelPassport.missingStyle',
    defaultMessage: 'Pick the feeling you want to travel for.',
    description: 'Validation message when no travel style is chosen',
  },
  missingWeather: {
    id: 'travelPassport.missingWeather',
    defaultMessage: 'Choose the weather you want to wake up to.',
    description: 'Validation message when no weather preference is chosen',
  },
  missingVisa: {
    id: 'travelPassport.missingVisa',
    defaultMessage: 'Tell us which visa lane you’re in — “I’ll check” is fine.',
    description: 'Validation message when no visa status is chosen',
  },
  missingDuration: {
    id: 'travelPassport.missingDuration',
    defaultMessage: 'Choose a rough trip length.',
    description: 'Validation message when no valid trip duration is chosen',
  },
  missingBudget: {
    id: 'travelPassport.missingBudget',
    defaultMessage: 'Choose a rough budget range.',
    description: 'Validation message when no valid budget is chosen',
  },
  missingIdentity: {
    id: 'travelPassport.missingIdentity',
    defaultMessage:
      'Add your name, demo passport details, departure city and how we should address you.',
    description: 'Validation message for the final demo document step',
  },
  daysValue: {
    id: 'travelPassport.daysValue',
    defaultMessage: '{days} days',
    description: 'Formatted duration in the trip signal summary',
  },
  trailTitle: {
    id: 'travelPassport.trailTitle',
    defaultMessage: 'Your trip trail',
    description: 'Heading above the journey map',
  },
  trailHint: {
    id: 'travelPassport.trailHint',
    defaultMessage: 'Pick a path and your traveller walks it. Stamps land on the card below.',
    description: 'Supporting line explaining how the journey board works',
  },
  stopsCleared: {
    id: 'travelPassport.stopsCleared',
    defaultMessage: '{count} of {total} stops cleared',
    description: 'Count of completed stops on the journey map',
  },
  stopAria: {
    id: 'travelPassport.stopAria',
    defaultMessage: 'Walk to stop {index}: {label}',
    description: 'Accessible label for a stop marker on the journey map',
  },
  finishTitle: {
    id: 'travelPassport.finishTitle',
    defaultMessage: 'Last stamp before the map',
    description: 'Heading on the reward panel at the final stop',
  },
  finishHint: {
    id: 'travelPassport.finishHint',
    defaultMessage:
      'Stamp the card and we walk your six picks into a shortlist of real places.',
    description: 'Supporting line on the reward panel at the final stop',
  },
  boardAria: {
    id: 'travelPassport.boardAria',
    defaultMessage: 'Choose your path: {label}',
    description: 'Accessible label for the branching choice board',
  },
  trailhead: {
    id: 'travelPassport.trailhead',
    defaultMessage: 'You are here',
    description: 'Label on the board marker the traveller starts each stop from',
  },
  stampsTitle: {
    id: 'travelPassport.stampsTitle',
    defaultMessage: 'Stamps collected',
    description: 'Heading above the collected picks rail',
  },
  stampLocked: {
    id: 'travelPassport.stampLocked',
    defaultMessage: 'Not stamped',
    description: 'Placeholder on a stamp slot with no answer yet',
  },
  walkOn: {
    id: 'travelPassport.walkOn',
    defaultMessage: 'Walk on',
    description: 'Button that moves the traveller to the next stop',
  },
  pickToWalk: {
    id: 'travelPassport.pickToWalk',
    defaultMessage: 'Pick a path to walk on',
    description: 'Hint shown in place of the walk-on button while a stop is unanswered',
  },
});

export { messages as travelPassportMessages };

type RoundId = 'style' | 'weather' | 'visa' | 'duration' | 'budget' | 'identity';

type RoundDefinition = {
  id: RoundId;
  label: MessageDescriptor;
  prompt: MessageDescriptor;
  hint: MessageDescriptor;
  Icon: Icon;
};

const ROUND_DEFINITIONS: RoundDefinition[] = [
  {
    id: 'style',
    label: messages.styleRound,
    prompt: messages.stylePrompt,
    hint: messages.styleHint,
    Icon: Compass,
  },
  {
    id: 'weather',
    label: messages.weatherRound,
    prompt: messages.weatherPrompt,
    hint: messages.weatherHint,
    Icon: CloudSun,
  },
  {
    id: 'visa',
    label: messages.visaRound,
    prompt: messages.visaPrompt,
    hint: messages.visaHint,
    Icon: GlobeHemisphereWest,
  },
  {
    id: 'duration',
    label: messages.durationRound,
    prompt: messages.durationPrompt,
    hint: messages.durationHint,
    Icon: Clock,
  },
  {
    id: 'budget',
    label: messages.budgetRound,
    prompt: messages.budgetPrompt,
    hint: messages.budgetHint,
    Icon: ChartBar,
  },
  {
    id: 'identity',
    label: messages.identityRound,
    prompt: messages.identityPrompt,
    hint: messages.identityHint,
    Icon: IdentificationCard,
  },
];

const STYLE_OPTIONS: {
  id: string;
  label: MessageDescriptor;
  detail: MessageDescriptor;
  image: string;
}[] = [
  {
    id: 'sea',
    label: messages.styleSea,
    detail: messages.styleSeaDetail,
    image: STYLE_IMAGES.sea,
  },
  {
    id: 'extreme',
    label: messages.styleExtreme,
    detail: messages.styleExtremeDetail,
    image: STYLE_IMAGES.extreme,
  },
  {
    id: 'civilisation',
    label: messages.styleCivilisation,
    detail: messages.styleCivilisationDetail,
    image: STYLE_IMAGES.civilisation,
  },
  {
    id: 'greenery',
    label: messages.styleGreenery,
    detail: messages.styleGreeneryDetail,
    image: STYLE_IMAGES.greenery,
  },
  {
    id: 'business',
    label: messages.styleBusiness,
    detail: messages.styleBusinessDetail,
    image: STYLE_IMAGES.business,
  },
];

const WEATHER_OPTIONS: {
  id: string;
  label: MessageDescriptor;
  detail: MessageDescriptor;
  Icon: Icon;
  image: string;
}[] = [
  {
    id: 'summer',
    label: messages.weatherSummer,
    detail: messages.weatherSummerDetail,
    Icon: Sun,
    image: SEASON_IMAGES.summer,
  },
  {
    id: 'spring',
    label: messages.weatherSpring,
    detail: messages.weatherSpringDetail,
    Icon: Leaf,
    image: SEASON_IMAGES.spring,
  },
  {
    id: 'autumn',
    label: messages.weatherAutumn,
    detail: messages.weatherAutumnDetail,
    Icon: CloudSun,
    image: SEASON_IMAGES.autumn,
  },
  {
    id: 'winter',
    label: messages.weatherWinter,
    detail: messages.weatherWinterDetail,
    Icon: Snowflake,
    image: SEASON_IMAGES.winter,
  },
];

const VISA_OPTIONS: {
  id: string;
  label: MessageDescriptor;
  detail: MessageDescriptor;
  Icon: Icon;
  image: string;
}[] = [
  {
    id: 'yes',
    label: messages.visaYes,
    detail: messages.visaYesDetail,
    Icon: AirplaneTilt,
    image: VISA_IMAGES.yes,
  },
  {
    id: 'no',
    label: messages.visaNo,
    detail: messages.visaNoDetail,
    Icon: ShieldCheck,
    image: VISA_IMAGES.no,
  },
  {
    id: 'unsure',
    label: messages.visaUnsure,
    detail: messages.visaUnsureDetail,
    Icon: GlobeHemisphereWest,
    image: VISA_IMAGES.unsure,
  },
];

const DURATION_OPTIONS: {
  id: string;
  label: MessageDescriptor;
  detail: MessageDescriptor;
  Icon: Icon;
  days: string;
  image: string;
}[] = [
  {
    id: '4',
    label: messages.durationLongWeekend,
    detail: messages.durationLongWeekendDetail,
    Icon: Clock,
    days: '4 days',
    image: DURATION_IMAGES['4'],
  },
  {
    id: '7',
    label: messages.durationWeek,
    detail: messages.durationWeekDetail,
    Icon: CalendarBlank,
    days: '7 days',
    image: DURATION_IMAGES['7'],
  },
  {
    id: '10',
    label: messages.durationTen,
    detail: messages.durationTenDetail,
    Icon: CalendarBlank,
    days: '10 days',
    image: DURATION_IMAGES['10'],
  },
  {
    id: '14',
    label: messages.durationFortnight,
    detail: messages.durationFortnightDetail,
    Icon: AirplaneTilt,
    days: '14+ days',
    image: DURATION_IMAGES['14'],
  },
];

const BUDGET_OPTIONS: {
  id: string;
  label: MessageDescriptor;
  detail: MessageDescriptor;
  Icon: Icon;
  amount: string;
  image: string;
}[] = [
  {
    id: '800',
    label: messages.budgetSimple,
    detail: messages.budgetSimpleDetail,
    Icon: Wallet,
    amount: '800',
    image: BUDGET_IMAGES['800'],
  },
  {
    id: '1400',
    label: messages.budgetComfortable,
    detail: messages.budgetComfortableDetail,
    Icon: CurrencyCircleDollar,
    amount: '1,400',
    image: BUDGET_IMAGES['1400'],
  },
  {
    id: '2500',
    label: messages.budgetTreat,
    detail: messages.budgetTreatDetail,
    Icon: Sparkle,
    amount: '2,500',
    image: BUDGET_IMAGES['2500'],
  },
  {
    id: '4000',
    label: messages.budgetBig,
    detail: messages.budgetBigDetail,
    Icon: Mountains,
    amount: '4,000+',
    image: BUDGET_IMAGES['4000'],
  },
];

const GENDER_OPTIONS: { id: string; label: MessageDescriptor; image: string }[] = [
  { id: 'male', label: messages.genderMale, image: TRAVELLER_IMAGES.male },
  { id: 'female', label: messages.genderFemale, image: TRAVELLER_IMAGES.female },
];

const DEFAULT_GENDER = 'male';

/** How many children the stepper allows on the document. */
const CHILDREN_MAX = 8;
/** Custom budgets move in practical hundred-dollar steps and stay bounded. */
const CUSTOM_BUDGET_STEP = 100;
const CUSTOM_BUDGET_MAX = 100000;

/*
 * The traveller card's inputs sit on the same dark ground as the panel beside it,
 * so they use the site's own field treatment rather than the light-panel one.
 */
const PANEL_FIELD_CLASS =
  'w-full rounded-input border border-border bg-muted/40 px-3 py-2.5 text-base text-foreground transition-colors duration-150 placeholder:text-muted-foreground-subtle focus-visible:border-primary focus-visible:outline-none';

/** The − / + keys of the children counter: square, quiet, and big enough to tap. */
const STEPPER_BUTTON_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-foreground transition-colors duration-150 [touch-action:manipulation] hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

/** The two text controls that select the traveller illustration shown beside the form. */
const GENDER_OPTION_CLASS =
  'flex-1 rounded-input border border-border bg-muted/40 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors duration-150 [touch-action:manipulation] hover:border-primary/60 hover:text-foreground data-[selected=true]:border-primary data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary';

/** The compact − / + keys inside the custom duration card. */
const CUSTOM_DURATION_STEP_CLASS =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary bg-muted p-0 text-card-foreground leading-none transition-colors duration-150 [touch-action:manipulation] hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary';

/** One supplied cut-out, shown alone in the left visual column for the active choice. */
const TRAVELLER_FIGURE_CLASS =
  'h-[480px] w-full max-w-[300px] self-end object-contain object-bottom';

/** The male artwork has more transparent headroom, so give its visible figure equal weight. */
const TRAVELLER_FIGURE_MALE_CLASS =
  'h-[520px] w-full max-w-[330px] self-end object-contain object-bottom';

type BoardPoint = { x: number; y: number };

/** One walkable path leaving the trailhead at the current stop. */
type BoardOption = {
  id: string;
  title: string;
  detail?: string;
  meta?: string;
  brightMeta?: boolean;
  accentCircle?: boolean;
  image?: string;
  custom?: boolean;
  customValue?: number;
  customMin?: number;
  customMax?: number;
  customUnit?: string;
  customDecreaseLabel?: string;
  customIncreaseLabel?: string;
  onCustomStep?: (delta: number) => void;
  Icon: Icon;
};

/** A slot on the stamp card under the board. */
type StampSlot = {
  id: RoundId;
  label: string;
  value: string;
  done: boolean;
  Icon: Icon;
};

/**
 * Board geometry, lifted 1:1 from the authored mockups (Idle / Moved). Every
 * number below is in the mockup's own 1144 × 605 space, and the whole board is
 * drawn as a single SVG that scales as one piece — so the rendered map matches
 * the drawings at any width.
 *
 * What the viewport shows, though, is the plate alone: 1130 × 591. The mockup
 * carried a 7-unit gutter outside the frame, which read as padding once the
 * board lost its panel, so the drawing is shifted up and left by exactly that
 * much and the frame now meets the edge. Coordinates below stay authored —
 * nothing is renumbered — because one translate on the content does the work.
 */
const BOARD_W = 1130;
const BOARD_H = 591;

/** Inner map plate and its rounded frame. */
const PLATE = { x: 7, y: 7, width: 1130, height: 591, radius: 15 };

/** The gutter trimmed off the authored space, applied as a single shift. */
const BOARD_SHIFT = `translate(${-PLATE.x} ${-PLATE.y})`;

/** Vertical map rules, and the frame ticks that line up with them. */
const RULES = [289, 572, 855];

/** The row the left and right frame ticks sit on. */
const TICK_ROW = 302;

/** One option card: box, medallion and the corner radius from the mockup. */
const CARD = { width: 242, height: 85, radius: 15 };
const MEDALLION = { ring: 43, photo: 39 };

/** Waypoint dots where a trail meets a card, and the junction dot left behind. */
const DOT_R = 5.5;
const HUB_DOT_R = 12;

/** Where the traveller stands before setting off, and the junction he leaves. */
const TRAILHEAD: BoardPoint = { x: 129, y: 301 };
const TRAIL_HUB: BoardPoint = { x: 130, y: 301 };

/** The window the ship is drawn through, mid-board. */
const SHIP_WINDOW = { x: 533, y: 58, width: 150, height: 370 };

/** Side of one square frame in the walker atlas, in board units. */
const WALK_FRAME = 78;

/**
 * Walking pace, in board units per second, and its floor and ceiling in ms.
 * Deliberately unhurried: he is a traveller crossing a map, and the stop waits
 * for him, so the walk is the moment rather than a transition to sit through.
 */
const WALK_SPEED = 235;
const WALK_MIN_MS = 900;
const WALK_MAX_MS = 3800;

/** How quickly his heading catches up with the trail, as a time constant in ms. */
const TURN_EASE_MS = 110;

/**
 * A place on the board. The first three slots are the near cards, one per fork
 * out of the trailhead; slots 3-5 sit further along those same three forks, so
 * reaching one means walking past its near card.
 */
type BoardSlot = {
  /** Top-left corner of the card box. */
  card: BoardPoint;
  /** Centre of the medallion hanging off the card's left edge — where he lands. */
  avatar: BoardPoint;
  /** Dot where the trail meets this card. */
  entry: BoardPoint;
  /** Dot the onward trail leaves from, when a further card shares this fork. */
  exit?: BoardPoint;
  /** The dashed trail drawn for this slot. */
  trail: string;
  /** The line the traveller walks, from the trailhead onto the medallion. */
  walk: string;
};

const BOARD_SLOTS: BoardSlot[] = [
  {
    // Up the left fork
    card: { x: 280.5, y: 80.5 },
    avatar: { x: 289, y: 123 },
    entry: { x: 246, y: 123 },
    exit: { x: 523, y: 122 },
    trail: 'M297.5 116C205.268 116 130 190.768 130 283',
    walk: 'M129 301L130 283C130 190.768 205.268 116 289 123',
  },
  {
    // Down the left fork
    card: { x: 280.5, y: 438.5 },
    avatar: { x: 289, y: 481 },
    entry: { x: 246, y: 482 },
    exit: { x: 523, y: 482 },
    trail: 'M303.5 489H297C204.768 489 130 414.232 130 322',
    walk: 'M129 301L130 322C130 414.232 204.768 489 289 481',
  },
  {
    // Straight on
    card: { x: 330.5, y: 258.5 },
    avatar: { x: 339, y: 301 },
    entry: { x: 296, y: 301 },
    exit: { x: 573, y: 301 },
    trail: 'M140.5 301.5L380.5 301.5',
    walk: 'M129 301L339 301',
  },
  {
    // Beyond the upper card
    card: { x: 847.5, y: 80.5 },
    avatar: { x: 856, y: 123 },
    entry: { x: 813, y: 123 },
    trail: 'M522 122H813',
    walk: 'M129 301L130 283C130 190.768 205.268 116 289 123L522 122L856 123',
  },
  {
    // Beyond the lower card
    card: { x: 845.5, y: 438.5 },
    avatar: { x: 854, y: 481 },
    entry: { x: 811, y: 481 },
    trail: 'M522 481H813',
    walk: 'M129 301L130 322C130 414.232 204.768 489 289 481L522 481L854 481',
  },
  {
    // Beyond the middle card
    card: { x: 790.5, y: 258.5 },
    avatar: { x: 799, y: 301 },
    entry: { x: 754, y: 301 },
    trail: 'M577 302L756.5 302',
    walk: 'M129 301L799 301',
  },
];

/** The near fork a far slot shares, or -1 when the slot is itself a near one. */
function nearForkOf(index: number): number {
  return index >= 3 ? index - 3 : -1;
}

/**
 * Which slots to use for a given number of choices, so the map stays balanced
 * when a stop has fewer than six. Four choices, for instance, keep the three
 * forks and send the last one further along the middle fork.
 */
const SLOT_ORDER: Record<number, number[]> = {
  1: [2],
  2: [0, 1],
  3: [0, 1, 2],
  4: [0, 1, 2, 5],
  5: [0, 1, 2, 3, 4],
  6: [0, 1, 2, 3, 4, 5],
};

const DEFAULT_SLOT_ORDER = [0, 1, 2, 3, 4, 5];

/** The walker's resting pose: pixel position plus the heading he faces. */
type WalkerPose = { x: number; y: number; angle: number };
const IDLE_POSE: WalkerPose = { x: TRAILHEAD.x, y: TRAILHEAD.y, angle: 0 };

function poseTransform(pose: WalkerPose): string {
  return `translate(${pose.x} ${pose.y}) rotate(${pose.angle})`;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}


/**
 * The sailing boat moored mid-board, drawn from the mockup at 90% scale and seen
 * through a fixed window.
 *
 * It rocks rather than bobs: the pivot sits down at the hull's waterline, so a
 * couple of degrees of roll sweeps the mast through a slow arc while the boat
 * stays where it is moored — the way a hull actually answers a wave.
 */
const ShipMark: FC<{ uid: string }> = ({ uid }) => (
  <g clipPath={`url(#${uid}-ship)`} aria-hidden="true">
    <g
      className="animate-ship-drift motion-reduce:animate-none"
      style={{ transformBox: 'view-box', transformOrigin: '596px 412px' }}
    >
      <path
        d="M669.665 287.122C669.15 288.46 668.368 290.421 667.304 292.905C665.175 297.874 661.914 304.938 657.397 313.311C648.36 330.057 634.295 352.031 614.176 372.945C574.012 414.694 546.675 425.666 541.596 427.448C541.254 427.567 540.876 427.474 540.633 427.21L540.632 427.209L533.739 419.754C533.514 419.509 533.442 419.158 533.552 418.84C535.404 413.545 548.059 381.891 599.117 337.434C618.436 320.614 636.445 307.673 649.623 298.937C656.212 294.569 661.593 291.252 665.325 289.028C667.191 287.917 668.646 287.078 669.633 286.518C669.749 286.452 669.859 286.39 669.962 286.332C669.874 286.564 669.778 286.829 669.665 287.122Z"
        fill="#121417"
        stroke="#131416"
      />
      <path
        d="M667.239 289.042C667.239 289.042 633.64 308.173 599.145 338.897C561.199 372.692 543.415 400.761 540.717 405.204C540.487 405.581 540.492 406.054 540.724 406.431C541.633 407.89 544.081 411.639 546.796 414.245C548.813 416.183 551.868 418.48 553.203 419.463C553.601 419.756 554.138 419.768 554.55 419.492C559.692 416.031 592.857 393.315 616.853 367.227C652.185 328.82 667.241 289.044 667.241 289.044L667.239 289.042Z"
        fill="#181A1C"
      />
      <path
        d="M653.112 303.725C653.112 303.725 631.157 313.322 605.587 335.857C565.774 370.948 552.91 392.439 550.108 397.726C549.694 398.508 549.755 399.452 550.265 400.173C551.238 401.549 552.962 403.863 554.684 405.516C556.3 407.068 558.179 408.325 559.409 409.075C560.198 409.557 561.199 409.524 561.958 408.997C568.016 404.802 595.448 385.347 617.844 362.041C642.191 336.703 653.115 303.722 653.115 303.722L653.112 303.725Z"
        fill="#272A2F"
      />
      <path
        d="M650.664 306.323C650.664 306.323 629.815 315.436 605.536 336.836C567.73 370.156 555.513 390.566 552.852 395.586C552.458 396.328 552.517 397.225 552.999 397.911C553.922 399.22 555.56 401.414 557.195 402.985C558.729 404.46 560.512 405.652 561.681 406.363C562.431 406.82 563.38 406.792 564.101 406.291C569.852 402.306 595.905 383.832 617.169 361.702C640.289 337.639 650.662 306.323 650.662 306.323H650.664Z"
        fill="#121417"
      />
      <path
        d="M562.871 402.739L557.666 397.738C557.024 397.122 556.991 396.106 557.593 395.45L568.554 383.486C569.267 382.711 570.475 382.671 571.234 383.402L576.672 388.625C577.431 389.353 577.44 390.566 576.693 391.306L565.162 402.725C564.53 403.35 563.513 403.359 562.871 402.741V402.739Z"
        fill="#272A2F"
      />
      <path
        d="M563.415 401.505L558.879 397.145C558.319 396.607 558.291 395.722 558.816 395.15L568.369 384.724C568.99 384.048 570.044 384.015 570.705 384.649L575.442 389.201C576.102 389.836 576.112 390.892 575.458 391.538L565.408 401.489C564.858 402.034 563.972 402.041 563.412 401.503L563.415 401.505Z"
        fill="#171717"
      />
      <path
        d="M588.287 365.563C587.085 364.406 587.036 362.5 588.179 361.285C591.187 358.087 596.687 352.28 599.089 350C602.275 346.972 611.095 339.484 614.236 336.827C614.878 336.284 615.824 336.314 616.431 336.897L621.393 341.664C622 342.247 622.068 343.191 621.55 343.853C619.018 347.096 611.877 356.197 608.975 359.499C606.789 361.985 601.199 367.705 598.121 370.835C596.952 372.025 595.043 372.051 593.839 370.894L588.289 365.56L588.287 365.563Z"
        fill="hsl(var(--primary))"
      />
      <path
        d="M590.069 364.301C588.994 363.268 588.952 361.563 589.971 360.479C592.658 357.623 597.571 352.437 599.714 350.4C602.56 347.696 610.439 341.006 613.243 338.634C613.817 338.15 614.663 338.178 615.204 338.698L619.636 342.957C620.177 343.476 620.238 344.322 619.777 344.912C617.514 347.808 611.137 355.937 608.543 358.885C606.592 361.107 601.6 366.216 598.85 369.011C597.805 370.074 596.099 370.098 595.026 369.065L590.069 364.301Z"
        fill="#171717"
      />
      <path
        d="M618.807 335.921L577.445 55.3993C577.421 55.2448 577.529 55.102 577.681 55.0809C577.834 55.0575 577.977 55.1629 578 55.3151L621.585 335.5C621.705 336.266 621.18 336.984 620.411 337.104C619.641 337.223 618.919 336.692 618.804 335.921H618.807Z"
        fill={`url(#${uid}-mast)`}
      />
      <path
        d="M618.434 339.775L575.177 384.741C570.344 288.756 572.364 184.888 579.64 74.9749L618.434 339.775Z"
        fill="#181A1C"
        fillOpacity={0.9}
      />
      <path
        d="M579.64 74.9749L618.434 339.775L603.228 355.581C595.774 293.788 588.708 185.085 579.64 74.9749Z"
        fill="#131416"
      />
      <path
        d="M623.471 334.537C633.343 224.752 614.372 142.669 581.078 72.554C634.067 127.007 674.356 178.475 681.951 230.833C689.049 279.77 659.067 311.341 623.471 334.535V334.537Z"
        fill="#181A1C"
        fillOpacity={0.9}
      />
      <path
        d="M623.471 334.537C624.052 327.986 624.544 322.135 624.623 319.976C721.869 252.865 645.993 150.922 613.123 107.103C649.798 149.054 675.981 189.669 681.953 230.835C689.051 279.77 659.07 311.344 623.473 334.537H623.471Z"
        fill="#131416"
      />
      <path
        d="M577.808 70.5263C555.45 68.0374 554.21 83.8768 539.714 86.4219C548.066 75.771 550.312 53.8159 576.196 58.8288L577.808 70.5263Z"
        fill="#FF0048"
      />
    </g>
  </g>
);

type QuestBoardProps = {
  stageKey: string;
  options: BoardOption[];
  selectedId: string;
  onPick: (id: string) => void;
  onArrive: () => void;
  label: string;
};

/** One leg of a walk: a trail, and the stretch of it to cover. */
type WalkStep = { index: number; from: number; to: number };

/** Where a point on a trail sits, and which way the trail runs there. */
function pointAt(path: SVGPathElement, length: number): BoardPoint {
  const total = path.getTotalLength();
  const point = path.getPointAtLength(Math.max(0, Math.min(total, length)));
  return { x: point.x, y: point.y };
}

/**
 * Heading of the trail at a point, in degrees, read over a long enough baseline
 * that curve sampling noise cannot make him twitch. Reversing flips it.
 */
function headingAt(path: SVGPathElement, length: number, reverse = false): number {
  const total = path.getTotalLength();
  const span = 14;
  const back = pointAt(path, Math.max(0, Math.min(total, length - span)));
  const ahead = pointAt(path, Math.max(0, Math.min(total, length + span)));
  const dx = reverse ? back.x - ahead.x : ahead.x - back.x;
  const dy = reverse ? back.y - ahead.y : ahead.y - back.y;
  if (dx === 0 && dy === 0) {
    return 0;
  }
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}

/** Position and heading at a point along a trail, facing the way he travels. */
function poseAt(path: SVGPathElement, length: number, reverse = false): WalkerPose {
  const point = pointAt(path, length);
  return { x: point.x, y: point.y, angle: headingAt(path, length, reverse) };
}

/** The nearest reading of `angle` to `near`, so a turn never wraps the long way. */function unwrap(angle: number, near: number): number {
  let value = angle;
  while (value - near > 180) {
    value -= 360;
  }
  while (near - value > 180) {
    value += 360;
  }
  return value;
}

/** Slow start, slow finish — the difference between walking and teleporting. */
function easeWalk(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * How far along a trail a point sits. Used to join two routes that share ground:
 * a far card's line runs out through its near card, so this finds the spot on it
 * where the traveller is already standing. Coarse sweep, then a fine pass — the
 * lines are short and this runs once per pick.
 */
function lengthNearest(path: SVGPathElement, target: BoardPoint): number {
  const total = path.getTotalLength();
  let best = 0;
  let bestGap = Number.POSITIVE_INFINITY;
  const sweep = (from: number, to: number, step: number): void => {
    for (let at = Math.max(0, from); at <= Math.min(total, to); at += step) {
      const point = pointAt(path, at);
      const gap = (point.x - target.x) ** 2 + (point.y - target.y) ** 2;
      if (gap < bestGap) {
        bestGap = gap;
        best = at;
      }
    }
  };
  sweep(0, total, 6);
  sweep(best - 6, best + 6, 0.5);
  return best;
}

/**
 * The play surface, drawn 1:1 with the authored board. The traveller stands at
 * the trailhead on the left; every choice is a card out on the map joined to him
 * by a walkable trail. Picking one walks him down it — the sprite sheet cycles
 * from its bottom frame upward — and the stop only moves on once he arrives.
 */
const QuestBoard: FC<QuestBoardProps> = ({
  stageKey,
  options,
  selectedId,
  onPick,
  onArrive,
  label,
}) => {
  const intl = useIntl();
  const rawId = useId();
  const uid = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
  const walkerRef = useRef<SVGGElement>(null);
  const trailRefs = useRef<(SVGPathElement | null)[]>([]);
  const frameRef = useRef<number | null>(null);
  /** Card he is resting at, or null while he is still at the trailhead. */
  const atRef = useRef<number | null>(null);
  /** His live pose, so a new walk can turn out of the heading he already has. */
  const poseRef = useRef<WalkerPose>(IDLE_POSE);
  const walkingRef = useRef(false);
  const settledRef = useRef<string>(selectedId);
  const stageRef = useRef<string>(stageKey);
  const arriveRef = useRef(onArrive);
  const [hovered, setHovered] = useState<number | null>(null);
  const [walking, setWalking] = useState(false);
  const [departed, setDeparted] = useState(false);

  arriveRef.current = onArrive;

  const order = (SLOT_ORDER[options.length] ?? DEFAULT_SLOT_ORDER).slice(0, options.length);
  const slots = order.map((slot) => BOARD_SLOTS[slot]);
  const selectedIndex = options.findIndex((option) => option.id === selectedId);
  const optionKey = options.map((option) => option.id).join('|');

  /** The choice whose card sits on the near fork of this one, or -1. */
  const forkOf = (index: number): number => {
    const slot = order[index];
    if (slot === undefined) {
      return -1;
    }
    const near = nearForkOf(slot);
    return near < 0 ? -1 : order.indexOf(near);
  };

  const applyPose = (pose: WalkerPose): void => {
    poseRef.current = pose;
    walkerRef.current?.setAttribute('transform', poseTransform(pose));
  };

  /** Where a trail ends: the medallion he steps onto. */
  const endLength = (index: number): number => {
    const path = trailRefs.current[index];
    return path ? path.getTotalLength() : 0;
  };

  const restPose = (index: number): WalkerPose => {
    const path = trailRefs.current[index];
    if (!path) {
      return IDLE_POSE;
    }
    return poseAt(path, path.getTotalLength());
  };

  const stopWalk = (): void => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    walkingRef.current = false;
  };

  useEffect(() => stopWalk, []);

  /**
   * Walks the whole route as one movement: back down the trail he is on, if any,
   * then out along the new one. Distance is eased end to end, so he leans into
   * the walk and settles out of it instead of snapping between legs, and his
   * heading chases the trail through a low-pass so corners read as turns rather
   * than flicks.
   */
  const runWalk = (steps: WalkStep[], startAngle: number): void => {
    const legs = steps
      .map((step) => ({ ...step, span: Math.abs(step.to - step.from) }))
      .filter((leg) => leg.span > 0.5);
    if (legs.length === 0) {
      walkingRef.current = false;
      setWalking(false);
      arriveRef.current();
      return;
    }
    const total = legs.reduce((sum, leg) => sum + leg.span, 0);
    const duration = Math.min(WALK_MAX_MS, Math.max(WALK_MIN_MS, (total / WALK_SPEED) * 1000));
    let angle = startAngle;
    let last = performance.now();
    const started = last;

    const tick = (now: number): void => {
      const elapsed = now - started;
      const progress = Math.min(1, elapsed / duration);
      let covered = easeWalk(progress) * total;
      let leg = legs[legs.length - 1];
      for (const candidate of legs) {
        if (covered <= candidate.span || candidate === leg) {
          leg = candidate;
          break;
        }
        covered -= candidate.span;
      }
      const path = trailRefs.current[leg.index];
      if (!path) {
        stopWalk();
        setWalking(false);
        arriveRef.current();
        return;
      }
      const reverse = leg.to < leg.from;
      const along = leg.from + (reverse ? -1 : 1) * Math.min(covered, leg.span);
      const point = pointAt(path, along);
      const target = unwrap(headingAt(path, along, reverse), angle);
      // Frame-rate independent easing towards the trail's heading.
      const step = 1 - Math.exp(-Math.max(1, now - last) / TURN_EASE_MS);
      last = now;
      angle += (target - angle) * step;
      applyPose({ x: point.x, y: point.y, angle });

      if (progress < 1) {
        frameRef.current = window.requestAnimationFrame(tick);
        return;
      }
      frameRef.current = null;
      walkingRef.current = false;
      setWalking(false);
      applyPose({ x: point.x, y: point.y, angle: target });
      arriveRef.current();
    };
    frameRef.current = window.requestAnimationFrame(tick);
  };

  /**
   * Picking a card sends him there from wherever he is standing.
   *
   * Two cards on the same fork share their ground, so he never retraces it: from
   * a near card he simply carries on outward along the shared line, and from a
   * far card he walks back down it and stops at the near one. Only a genuinely
   * different fork sends him back through the trailhead, because that is the one
   * case where there is no other way round.
   */
  const handlePick = (index: number): void => {
    const option = options[index];
    if (!option) {
      return;
    }
    settledRef.current = option.id;
    onPick(option.id);

    const from = atRef.current;
    atRef.current = index;
    setDeparted(true);
    stopWalk();

    if (from === index || prefersReducedMotion()) {
      applyPose(restPose(index));
      setWalking(false);
      arriveRef.current();
      return;
    }

    const steps: WalkStep[] = [];
    const onward = trailRefs.current[index];
    const behind = from === null ? null : trailRefs.current[from];
    if (from !== null && forkOf(index) === from && onward) {
      // He is standing on the near card of the one just picked: carry on outward.
      steps.push({ index, from: lengthNearest(onward, slots[from].avatar), to: endLength(index) });
    } else if (from !== null && forkOf(from) === index && behind) {
      // The card just picked is the near one on the line he is already out on.
      steps.push({ index: from, from: endLength(from), to: lengthNearest(behind, slots[index].avatar) });
    } else {
      if (from !== null) {
        steps.push({ index: from, from: endLength(from), to: 0 });
      }
      steps.push({ index, from: 0, to: endLength(index) });
    }
    walkingRef.current = true;
    setWalking(true);
    runWalk(steps, poseRef.current.angle);
  };

  const handleKey = (event: KeyboardEvent<SVGGElement>, index: number): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePick(index);
    }
  };

  /** Narrow screens have no map, so a pick lands without a walk. */
  const handleListPick = (id: string): void => {
    settledRef.current = id;
    onPick(id);
    arriveRef.current();
  };

  /**
   * A new stop, or an answer arriving from chat or a seeded surprise trip,
   * seats him without a walk — so the map never contradicts the data.
   */
  useEffect(() => {
    const stageChanged = stageRef.current !== stageKey;
    if (!stageChanged && (walkingRef.current || settledRef.current === selectedId)) {
      return;
    }
    stageRef.current = stageKey;
    settledRef.current = selectedId;
    stopWalk();
    setWalking(false);
    setHovered(null);
    const index = options.findIndex((option) => option.id === selectedId);
    atRef.current = index >= 0 ? index : null;
    setDeparted(index >= 0);
    applyPose(index >= 0 ? restPose(index) : IDLE_POSE);
    // biome-ignore lint/correctness/useExhaustiveDependencies: options are keyed by optionKey
  }, [stageKey, selectedId, optionKey]);

  return (
    <div className="min-w-0">
      {/* The map, drawn 1:1 with the authored board */}
      <svg
        viewBox={`0 0 ${BOARD_W} ${BOARD_H}`}
        className="hidden h-auto w-full select-none lg:block"
        role="group"
        aria-label={label}
      >
        <defs>
          <clipPath id={`${uid}-plate`}>
            <rect
              x={PLATE.x}
              y={PLATE.y}
              width={PLATE.width}
              height={PLATE.height}
              rx={PLATE.radius}
            />
          </clipPath>
          <filter
            id={`${uid}-glow`}
            x={233.5}
            y={0.5}
            width={724}
            height={724}
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation={112.75} />
          </filter>
          <filter
            id={`${uid}-halo`}
            x={53}
            y={-180}
            width={1071}
            height={1071}
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation={225.5} />
          </filter>
          {/* The accent shadow a standing card casts. Generous region: the blur
              needs room to spread past the card's own box. */}
          <filter
            id={`${uid}-cardglow`}
            x="-25%"
            y="-70%"
            width="150%"
            height="240%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation={11} />
          </filter>
          {/*
            The wave field, used only as a mask. White strokes because a mask is
            read by luminance: full white keeps every line at full strength, and
            the glow behind it decides how much of each one is actually seen.
          */}
          <mask
            id={`${uid}-waves`}
            maskUnits="userSpaceOnUse"
            x={BOARD_CONTOUR_FIELD.x}
            y={BOARD_CONTOUR_FIELD.y}
            width={BOARD_CONTOUR_FIELD.width}
            height={BOARD_CONTOUR_FIELD.height}
          >
            <g fill="none" stroke="#fff" strokeMiterlimit={10} strokeWidth={2}>
              {BOARD_CONTOURS.map((line, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static path field
                <path key={`wave-${index}`} d={line} />
              ))}
            </g>
          </mask>
          <linearGradient
            id={`${uid}-mast`}
            x1={599.838}
            y1={58.0015}
            x2={599.838}
            y2={340.044}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.8125} stopColor="#272A2F" />
            <stop offset={1} stopColor="hsl(var(--primary))" />
          </linearGradient>
          <clipPath id={`${uid}-ship`}>
            <rect
              x={SHIP_WINDOW.x}
              y={SHIP_WINDOW.y}
              width={SHIP_WINDOW.width}
              height={SHIP_WINDOW.height}
            />
          </clipPath>
          <filter id={`${uid}-lift`} x="-15%" y="-45%" width="130%" height="220%">
            <feDropShadow dx={0} dy={4} stdDeviation={14.5} floodOpacity={0.45} />
          </filter>
          {/* The sprite window: one frame of the walk atlas, in the atlas's own
              upright frame — the walker group turns it to face the way he goes. */}
          <clipPath id={`${uid}-walker`}>
            <rect x={-39} y={-19} width={WALK_FRAME} height={WALK_FRAME} />
          </clipPath>
          {slots.map((slot, index) => (
            <clipPath key={`medal-${options[index].id}`} id={`${uid}-medal-${index}`}>
              <circle cx={slot.avatar.x} cy={slot.avatar.y} r={MEDALLION.photo} />
            </clipPath>
          ))}
        </defs>

        <g clipPath={`url(#${uid}-plate)`} transform={BOARD_SHIFT}>
          <rect
            x={PLATE.x}
            y={PLATE.y}
            width={PLATE.width}
            height={PLATE.height}
            rx={PLATE.radius}
            fill="hsl(var(--background))"
          />
          <g filter={`url(#${uid}-halo)`} aria-hidden="true">
            <circle cx={588.5} cy={355.5} r={84.5} fill="hsl(var(--primary))" fillOpacity={0.5} />
          </g>
          {/*
            Green line waves. The contour field is the mask and the pool of light
            under the ship is what shines through it, exactly as the board is
            composed: the water is only visible where the glow reaches, and fades
            out well before the frame.
          */}
          <g mask={`url(#${uid}-waves)`} aria-hidden="true">
            <g filter={`url(#${uid}-glow)`}>
              <circle cx={595.5} cy={362.5} r={136.5} fill="hsl(var(--primary))" />
            </g>
          </g>
          {RULES.map((x) => (
            <path
              key={`rule-${x}`}
              d={`M${x} 7.75V592.75`}
              stroke="hsl(var(--border))"
              fill="none"
            />
          ))}

          {/* Trails: dashed and unlit, as drawn — the traveller is the marker */}
          {slots.map((slot, index) => {
            /**
             * A trail lights up for the card being considered and for the card
             * he is standing on, plus the near card either runs through, so the
             * whole walked line reads as one route rather than a lit stub.
             */
            const cued = hovered ?? selectedIndex;
            const warm = cued !== null && cued >= 0 && (cued === index || forkOf(cued) === index);
            const flowing = warm && (hovered !== null || walking);
            return (
              <path
                key={`trail-${options[index].id}`}
                d={slot.trail}
                fill="none"
                strokeWidth={2}
                strokeDasharray="7 7"
                stroke={warm ? 'hsl(var(--primary) / 0.55)' : 'hsl(var(--border))'}
                className={`transition-[stroke] duration-200 motion-reduce:transition-none ${flowing ? 'animate-trail-flow' : ''} motion-reduce:animate-none`}
              />
            );
          })}

          {/* The lines he actually walks: measured, never painted */}
          <g fill="none" stroke="none" aria-hidden="true">
            {slots.map((slot, index) => (
              <path
                key={`walk-${options[index].id}`}
                d={slot.walk}
                ref={(element) => {
                  trailRefs.current[index] = element;
                }}
              />
            ))}
          </g>

          {/* The boat riding the swell mid-board */}
          <ShipMark uid={uid} />

          {/* The choices, out on the map */}
          {slots.map((slot, index) => {
            const option = options[index];
            const selected = index === selectedIndex;
            /**
             * Lit means "he is standing here, or he would if you clicked": the
             * stop he settled on reads exactly like a hover, so the board always
             * shows where the traveller is without a second visual language.
             */
            const lit = selected || hovered === index;
            const OptionIcon = option.Icon;
            return (
              <g
                key={option.id}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-label={option.detail ? `${option.title}. ${option.detail}` : option.title}
                onClick={() => handlePick(index)}
                onKeyDown={(event) => handleKey(event, index)}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered((current) => (current === index ? null : current))}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered((current) => (current === index ? null : current))}
                className="group cursor-pointer outline-none"
              >
                <rect
                  x={slot.card.x - 5}
                  y={slot.card.y - 5}
                  width={CARD.width + 10}
                  height={CARD.height + 10}
                  rx={CARD.radius + 5}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  className="opacity-0 group-focus-visible:opacity-100"
                />
                {/* Accent glow from the active card, blurred behind its neutral surface */}
                {lit ? (
                  <g filter={`url(#${uid}-cardglow)`} aria-hidden="true">
                    <rect
                      x={slot.card.x}
                      y={slot.card.y}
                      width={CARD.width}
                      height={CARD.height}
                      rx={CARD.radius}
                      fill="hsl(var(--primary))"
                      fillOpacity={0.45}
                    />
                  </g>
                ) : null}
                <rect
                  x={slot.card.x}
                  y={slot.card.y}
                  width={CARD.width}
                  height={CARD.height}
                  rx={CARD.radius}
                  fill="hsl(var(--travel-board-card))"
                  stroke={lit ? 'hsl(var(--primary))' : 'hsl(var(--border))'}
                  filter={`url(#${uid}-lift)`}
                  className="transition-[stroke] duration-200 group-hover:stroke-primary motion-reduce:transition-none"
                />
                <circle
                  cx={slot.avatar.x}
                  cy={slot.avatar.y}
                  r={MEDALLION.ring}
                  fill="hsl(var(--travel-board-card))"
                  stroke={lit || option.accentCircle ? 'hsl(var(--primary))' : 'hsl(var(--border))'}
                  className="transition-[stroke] duration-200 group-hover:stroke-primary motion-reduce:transition-none"
                />
                {option.image ? (
                  <image
                    href={option.image}
                    x={slot.avatar.x - MEDALLION.photo}
                    y={slot.avatar.y - MEDALLION.photo}
                    width={MEDALLION.photo * 2}
                    height={MEDALLION.photo * 2}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#${uid}-medal-${index})`}
                  />
                ) : (
                  <g
                    transform={`translate(${slot.avatar.x - 17} ${slot.avatar.y - 17})`}
                    className={selected ? 'text-primary' : 'text-muted-foreground'}
                  >
                    <OptionIcon size={34} weight="duotone" aria-hidden="true" />
                  </g>
                )}
                <foreignObject
                  x={slot.card.x + 68}
                  y={slot.card.y + 12}
                  width={CARD.width - 82}
                  height={CARD.height - 24}
                  className={option.custom ? 'pointer-events-auto' : 'pointer-events-none'}
                >
                  {option.custom ? (
                    <div className="flex h-full flex-col justify-center">
                      <p className="truncate text-[15px] font-bold leading-tight text-card-foreground">
                        {option.title}
                      </p>
                      <div className="mt-2 flex items-center gap-1.5 text-card-foreground">
                        <button
                          type="button"
                          aria-label={option.customDecreaseLabel ?? intl.formatMessage(messages.durationCustomFewer)}
                          disabled={(option.customValue ?? 0) <= (option.customMin ?? 0)}
                          onClick={(event) => {
                            event.stopPropagation();
                            option.onCustomStep?.(-1);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                          className={CUSTOM_DURATION_STEP_CLASS}
                        >
                          <Minus size={11} weight="bold" aria-hidden="true" />
                        </button>
                        <span className="min-w-[2ch] text-center font-consolas text-[12px] font-bold leading-none tabular-nums text-card-foreground">
                          {option.customValue ?? 0}
                        </span>
                        <button
                          type="button"
                          aria-label={option.customIncreaseLabel ?? intl.formatMessage(messages.durationCustomMore)}
                          disabled={(option.customValue ?? 0) >= (option.customMax ?? 90)}
                          onClick={(event) => {
                            event.stopPropagation();
                            option.onCustomStep?.(1);
                          }}
                          onKeyDown={(event) => event.stopPropagation()}
                          className={CUSTOM_DURATION_STEP_CLASS}
                        >
                          <Plus size={11} weight="bold" aria-hidden="true" />
                        </button>
                        <span className="font-consolas text-[9.5px] font-semibold uppercase tracking-caps text-card-foreground">
                          {option.customUnit ?? intl.formatMessage(messages.durationDays)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-full flex-col justify-center">
                      <p className="truncate text-[15px] font-bold leading-tight text-card-foreground">
                        {option.title}
                      </p>
                      <p
                        className={`mt-1 text-[11.5px] leading-[1.3] text-muted-foreground ${
                          option.meta ? 'truncate' : 'line-clamp-2'
                        }`}
                      >
                        {option.detail}
                      </p>
                      {option.meta ? (
                        <p
                          className={`mt-1 truncate font-consolas text-[9.5px] font-semibold uppercase tracking-caps ${
                            option.brightMeta ? 'text-card-foreground' : 'text-muted-foreground-subtle'
                          }`}
                        >
                          {option.meta}
                        </p>
                      ) : null}
                    </div>
                  )}
                </foreignObject>
              </g>
            );
          })}

          {/* Waypoints where the trails meet the cards */}
          {slots.map((slot, index) => (
            <g key={`dots-${options[index].id}`} aria-hidden="true">
              <circle
                cx={slot.entry.x}
                cy={slot.entry.y}
                r={DOT_R}
                fill="hsl(var(--background))"
                stroke="hsl(var(--primary))"
              />
              {slot.exit && order.includes((order[index] ?? 0) + 3) ? (
                <circle
                  cx={slot.exit.x}
                  cy={slot.exit.y}
                  r={DOT_R}
                  fill="hsl(var(--background))"
                  stroke="hsl(var(--primary))"
                />
              ) : null}
            </g>
          ))}

          {/* The trailhead he set off from */}
          {departed ? (
            <g className="animate-fadeUp motion-reduce:animate-none" aria-hidden="true">
              <circle
                cx={TRAIL_HUB.x}
                cy={TRAIL_HUB.y}
                r={HUB_DOT_R}
                fill="hsl(var(--background))"
                stroke="hsl(var(--primary))"
              />
              <path
                d={`M${TRAIL_HUB.x - HUB_DOT_R} ${TRAIL_HUB.y}H${TRAIL_HUB.x - 28}`}
                fill="none"
                stroke="hsl(var(--primary))"
              />
            </g>
          ) : null}

          {/* The traveller: four atlas frames, cycling from the bottom up */}
          <g ref={walkerRef} transform={poseTransform(IDLE_POSE)} aria-hidden="true">
            <circle
              r={28}
              fill="hsl(var(--background))"
              stroke="hsl(var(--border))"
              strokeWidth={2}
            />
            {/* The atlas is drawn head-up, so a quarter turn puts him on course */}
            <g transform="rotate(90)">
              <g clipPath={`url(#${uid}-walker)`}>
                <image
                  href={WALKER_ATLAS}
                  x={-39}
                  y={-19 - WALK_FRAME * (WALKER_FRAMES - 1)}
                  width={WALK_FRAME}
                  height={WALK_FRAME * WALKER_FRAMES}
                  className={walking ? 'animate-walk-cycle' : undefined}
                  style={{ '--walk-frame': `${WALK_FRAME}px` } as CSSProperties}
                />
              </g>
            </g>
            <path
              d="M22 0L11.5 6.062L11.5 -6.062Z"
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          </g>
        </g>

        {/* Map frame, with the ticks that line up with the rules */}
        <rect
          x={7.5}
          y={7.5}
          width={1129}
          height={590}
          rx={19.5}
          fill="none"
          stroke="hsl(var(--border))"
          transform={BOARD_SHIFT}
        />
        <g stroke="hsl(var(--primary))" fill="none" transform={BOARD_SHIFT} aria-hidden="true">
          {RULES.map((x) => (
            <g key={`tick-${x}`}>
              <path d={`M${x - 7} 7H${x - 0.25}M${x + 6.5} 7H${x - 0.25}M${x - 0.25} 7V14.5`} />
              <path
                d={`M${x - 7} 596.5H${x - 0.25}M${x + 6.5} 596.5H${x - 0.25}M${x - 0.25} 596.5V589`}
              />
            </g>
          ))}
          <path
            d={`M7 ${TICK_ROW + 13}L7 ${TICK_ROW}M7 ${TICK_ROW - 13}L7 ${TICK_ROW}M7 ${TICK_ROW}L13.5 ${TICK_ROW}L20 ${TICK_ROW}`}
          />
          <path
            d={`M1137 ${TICK_ROW + 13}L1137 ${TICK_ROW}M1137 ${TICK_ROW - 13}L1137 ${TICK_ROW}M1137 ${TICK_ROW}L1130.5 ${TICK_ROW}L1124 ${TICK_ROW}`}
          />
        </g>
      </svg>

      {/* Narrow screens walk the same paths down a vertical trail */}
      <div className="relative pl-9 lg:hidden">
        <span
          className="pointer-events-none absolute bottom-4 left-[15px] top-4 border-l border-dashed border-border"
          aria-hidden="true"
        />
        <div className="flex flex-col gap-2.5">
          {options.map((option) => {
            const selected = option.id === selectedId;
            const OptionIcon = option.Icon;
            const cardClassName =
              'group relative flex w-full items-center gap-3 rounded-[15px] border border-border bg-travel-board-card p-3 text-left transition duration-150 [touch-action:manipulation] data-[selected=true]:border-primary motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary';
            const cardContent = (
              <>
                <span
                  className="absolute -left-[31px] top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center"
                  aria-hidden="true"
                >
                  {selected ? (
                    <PersonSimpleWalk
                      size={20}
                      weight="fill"
                      className="animate-fadeUp text-primary motion-reduce:animate-none"
                    />
                  ) : (
                    <span className="h-3 w-3 rounded-full border-2 border-border bg-muted" />
                  )}
                </span>
                {option.image ? (
                  <span
                    className={`h-11 w-11 shrink-0 overflow-hidden ${
                      option.custom ? 'rounded-full border border-primary' : 'rounded-lg'
                    }`}
                  >
                    <img
                      src={option.image}
                      alt=""
                      loading="lazy"
                      width={200}
                      height={200}
                      className="h-full w-full object-cover"
                    />
                  </span>
                ) : (
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center bg-muted text-primary group-data-[selected=true]:bg-primary group-data-[selected=true]:text-primary-foreground ${
                      option.custom ? 'rounded-full border border-primary' : 'rounded-lg'
                    }`}
                  >
                    <OptionIcon size={22} weight="duotone" aria-hidden="true" />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-card-foreground">
                    {option.title}
                  </span>
                  {option.custom ? (
                    <span className="mt-1 flex items-center gap-1.5 text-card-foreground">
                      <button
                        type="button"
                        aria-label={option.customDecreaseLabel ?? intl.formatMessage(messages.durationCustomFewer)}
                        disabled={(option.customValue ?? 0) <= (option.customMin ?? 0)}
                        onClick={(event) => {
                          event.stopPropagation();
                          option.onCustomStep?.(-1);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        className={CUSTOM_DURATION_STEP_CLASS}
                      >
                        <Minus size={11} weight="bold" aria-hidden="true" />
                      </button>
                      <span className="min-w-[2ch] text-center font-consolas text-xs font-bold leading-none tabular-nums text-card-foreground">
                        {option.customValue ?? 0}
                      </span>
                      <button
                        type="button"
                        aria-label={option.customIncreaseLabel ?? intl.formatMessage(messages.durationCustomMore)}
                        disabled={(option.customValue ?? 0) >= (option.customMax ?? 90)}
                        onClick={(event) => {
                          event.stopPropagation();
                          option.onCustomStep?.(1);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        className={CUSTOM_DURATION_STEP_CLASS}
                      >
                        <Plus size={11} weight="bold" aria-hidden="true" />
                      </button>
                      <span className="font-consolas text-[9.5px] font-semibold uppercase tracking-caps text-card-foreground">
                        {option.customUnit ?? intl.formatMessage(messages.durationDays)}
                      </span>
                    </span>
                  ) : option.detail ? (
                    <span className="mt-0.5 block line-clamp-2 text-xs leading-snug text-muted-foreground">
                      {option.detail}
                    </span>
                  ) : null}
                </span>
                {selected ? (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check size={13} weight="bold" aria-hidden="true" />
                  </span>
                ) : null}
              </>
            );
            return option.custom ? (
              <div
                key={option.id}
                role="button"
                tabIndex={0}
                data-selected={selected}
                aria-pressed={selected}
                aria-label={option.title}
                onClick={() => handleListPick(option.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleListPick(option.id);
                  }
                }}
                className={cardClassName}
              >
                {cardContent}
              </div>
            ) : (
              <button
                key={option.id}
                type="button"
                data-selected={selected}
                aria-pressed={selected}
                onClick={() => handleListPick(option.id)}
                className={cardClassName}
              >
                {cardContent}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

type StampRailProps = {
  slots: StampSlot[];
  current: number;
  onSelect: (index: number) => void;
};

/** The card the visitor fills: one stamp per stop, clickable to go back. */
const StampRail: FC<StampRailProps> = ({ slots, current, onSelect }) => {
  const intl = useIntl();
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {slots.map((slot, index) => {
        const SlotIcon = slot.Icon;
        const active = index === current;
        return (
          <button
            key={`${slot.id}-${slot.done ? 'done' : 'open'}`}
            type="button"
            onClick={() => onSelect(index)}
            aria-current={active ? 'step' : undefined}
            aria-label={intl.formatMessage(messages.stopAria, {
              index: index + 1,
              label: slot.label,
            })}
            data-done={slot.done}
            data-active={active}
            className="group relative flex min-w-0 items-center gap-2.5 rounded-[15px] border border-dashed border-border bg-muted/20 px-2.5 py-2.5 text-left transition-colors duration-150 [touch-action:manipulation] hover:border-muted-foreground/60 data-[active=true]:border-solid data-[active=true]:border-primary data-[done=true]:border-solid data-[done=true]:border-primary/45 data-[done=true]:bg-primary/5 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                slot.done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              } ${slot.done ? 'animate-fadeUp motion-reduce:animate-none' : ''}`}
            >
              {slot.done ? (
                <Check size={15} weight="bold" aria-hidden="true" />
              ) : (
                <SlotIcon size={16} weight="duotone" aria-hidden="true" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-consolas text-[11px] font-semibold uppercase tracking-caps text-muted-foreground-subtle">
                {slot.label}
              </span>
              <span
                className={`mt-0.5 block truncate text-sm font-bold ${
                  slot.done ? 'text-foreground' : 'text-muted-foreground-subtle'
                }`}
              >
                {slot.value}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const TravelPassport: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const api = useContext(A2uiSurfaceContext);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const surnameRef = useRef<HTMLInputElement>(null);
  const passportNumberRef = useRef<HTMLInputElement>(null);
  const dateOfBirthRef = useRef<HTMLInputElement>(null);
  const originRef = useRef<HTMLInputElement>(null);
  const advanceOnArrival = useRef(false);
  const holdTimer = useRef<number | null>(null);
  const touched = useRef(false);
  const [round, setRound] = useState(0);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(
    () => () => {
      if (holdTimer.current !== null) {
        window.clearTimeout(holdTimer.current);
      }
    },
    [],
  );

  const title = str(node.props.title);
  const subtitle = str(node.props.subtitle);

  const read = (path: string): string => str(api?.getValue(path));
  const write = (path: string, value: string): void => api?.setValue(path, value);

  /**
   * The twelve answers, read from this surface's own data model.
   *
   * Single-segment pointers on purpose. A render's `dataModel` seed splits by
   * key shape: a pointer-form key (leading slash) is written to the document
   * ROOT, while a bare key seeds THIS surface's node — the one scoped reads and
   * the visitor's own picks share. So a nested pointer like `/trip/style` can
   * never be seeded where it is read, and an agent-supplied answer would land
   * out of sight. Flat names keep the seed and the read on the same node.
   */
  const firstName = read('/firstName');
  const surname = read('/surname');
  const passportNumber = read('/passportNumber');
  const dateOfBirth = read('/dateOfBirth');
  const storedGender = read('/gender');
  const gender = storedGender || DEFAULT_GENDER;
  const visa = read('/visa');
  const origin = read('/origin');
  const tripStyle = read('/style');
  const season = read('/season');
  const budget = read('/budget');
  const days = read('/days');

  useEffect(() => {
    if (storedGender.length === 0) {
      api?.setValue('/gender', DEFAULT_GENDER);
      api?.commitValue();
    }
  }, [api, storedGender]);

  /*
   * Children is the one numeric answer. An unset or unparseable value reads as
   * zero, so the stepper always shows a real number and the agent can seed it as
   * a plain string.
   */
  const childrenRaw = Number.parseInt(read('/children'), 10);
  const childCount = Number.isFinite(childrenRaw)
    ? Math.min(Math.max(childrenRaw, 0), CHILDREN_MAX)
    : 0;

  const choose = (path: string, value: string): void => {
    write(path, value);
    api?.commitValue();
    setShowErrors(false);
  };

  /**
   * Picking a card writes the answer straight away; the board then walks the
   * traveller over and calls back on arrival. Re-picking an answered stop keeps
   * the visitor where they are so they can see the change.
   */
  const pickOnBoard = (path: string, value: string, wasAnswered: boolean): void => {
    touched.current = true;
    choose(path, value);
    advanceOnArrival.current = !wasAnswered;
  };

  /** The traveller has reached the card: hold the moment, then open the next stop. */
  const handleArrival = (): void => {
    if (!advanceOnArrival.current) {
      return;
    }
    advanceOnArrival.current = false;
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
    }
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      setRound((current) => Math.min(current + 1, ROUND_DEFINITIONS.length - 1));
    }, 460);
  };

  const goToRound = (index: number): void => {
    touched.current = true;
    advanceOnArrival.current = false;
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    setShowErrors(false);
    setRound(index);
  };

  const handleText =
    (path: string) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      write(path, event.target.value);
      setShowErrors(false);
    };

  const budgetNumber = Number(budget);
  const daysNumber = Number(days);
  const validBudget = Number.isFinite(budgetNumber) && budgetNumber > 0;
  const validDays = Number.isInteger(daysNumber) && daysNumber > 0 && daysNumber <= 90;

  const roundReady = (roundId: RoundId): boolean => {
    switch (roundId) {
      case 'style':
        return tripStyle.length > 0;
      case 'weather':
        return season.length > 0;
      case 'visa':
        return visa.length > 0;
      case 'duration':
        return validDays;
      case 'budget':
        return validBudget;
      case 'identity':
        return (
          firstName.trim().length > 0 &&
          surname.trim().length > 0 &&
          passportNumber.trim().length > 0 &&
          dateOfBirth.trim().length > 0 &&
          origin.trim().length > 0 &&
          gender.length > 0
        );
    }
  };

  const currentRound = ROUND_DEFINITIONS[round] ?? ROUND_DEFINITIONS[0];
  const currentRoundReady = roundReady(currentRound.id);
  const completedRounds = ROUND_DEFINITIONS.filter((definition) => roundReady(definition.id)).length;
  const firstOpenRound = ROUND_DEFINITIONS.findIndex((definition) => !roundReady(definition.id));

  /**
   * Answers can also arrive from chat or from a seeded surprise trip. Until the
   * visitor touches the board themselves, the walker stands at the first stop
   * that is still open so the map never contradicts the data.
   */
  useEffect(() => {
    if (touched.current) {
      return;
    }
    setRound(firstOpenRound === -1 ? ROUND_DEFINITIONS.length - 1 : firstOpenRound);
  }, [firstOpenRound]);

  const handleNext = (): void => {
    if (!currentRoundReady) {
      setShowErrors(true);
      if (currentRound.id === 'identity') {
        if (firstName.trim().length === 0) {
          firstNameRef.current?.focus();
        } else if (surname.trim().length === 0) {
          surnameRef.current?.focus();
        } else if (passportNumber.trim().length === 0) {
          passportNumberRef.current?.focus();
        } else if (dateOfBirth.trim().length === 0) {
          dateOfBirthRef.current?.focus();
        } else {
          originRef.current?.focus();
        }
      }
      return;
    }

    setShowErrors(false);
    if (round < ROUND_DEFINITIONS.length - 1) {
      setRound((current) => current + 1);
      return;
    }

    api?.commitValue();
    api?.dispatch('submitPassport', {
      firstName,
      surname,
      passportNumber,
      dateOfBirth,
      gender,
      children: String(childCount),
      visa,
      origin,
      style: tripStyle,
      season,
      budget,
      days,
    });
  };

  /**
   * The current stop's choices, expressed as places on the board. Each one is a
   * path the traveller can walk; picking it writes the contract value.
   */
  const boardStage = (): {
    options: BoardOption[];
    selected: string;
    pick: (id: string) => void;
  } => {
    switch (currentRound.id) {
      case 'style':
        return {
          options: STYLE_OPTIONS.map((option) => ({
            id: option.id,
            title: intl.formatMessage(option.label),
            detail: intl.formatMessage(option.detail),
            image: option.image,
            Icon: Compass,
          })),
          selected: tripStyle,
          pick: (id) => pickOnBoard('/style', id, tripStyle.length > 0),
        };
      case 'weather':
        return {
          options: WEATHER_OPTIONS.map((option) => ({
            id: option.id,
            title: intl.formatMessage(option.label),
            detail: intl.formatMessage(option.detail),
            image: option.image,
            Icon: option.Icon,
          })),
          selected: season,
          pick: (id) => pickOnBoard('/season', id, season.length > 0),
        };
      case 'visa':
        return {
          options: VISA_OPTIONS.map((option) => ({
            id: option.id,
            title: intl.formatMessage(option.label),
            detail: intl.formatMessage(option.detail),
            image: option.image,
            Icon: option.Icon,
          })),
          selected: visa,
          pick: (id) => pickOnBoard('/visa', id, visa.length > 0),
        };
      case 'duration': {
        const fixedDurationIds = new Set(DURATION_OPTIONS.map((option) => option.id));
        const customSelected = days.length > 0 && !fixedDurationIds.has(days);
        return {
          options: [
            ...DURATION_OPTIONS.map((option) => ({
              id: option.id,
              title: intl.formatMessage(option.label),
              detail: intl.formatMessage(option.detail),
              meta: option.days,
              brightMeta: true,
              accentCircle: true,
              image: option.image,
              Icon: option.Icon,
            })),
            {
              id: 'custom',
              title: intl.formatMessage(messages.durationCustom),
              custom: true,
              accentCircle: true,
              image: CUSTOM_TIME_IMAGE,
              customValue: validDays ? daysNumber : 0,
              onCustomStep: (delta: number) => {
                touched.current = true;
                advanceOnArrival.current = false;
                const next = Math.min(Math.max((validDays ? daysNumber : 0) + delta, 0), 90);
                choose('/days', String(next));
              },
              Icon: Clock,
            },
          ],
          selected: customSelected ? 'custom' : days,
          pick: (id) => {
            if (id === 'custom') {
              pickOnBoard('/days', validDays ? days : '0', validDays);
              return;
            }
            pickOnBoard('/days', id, validDays);
          },
        };
      }
      case 'budget': {
        const fixedBudgetIds = new Set(BUDGET_OPTIONS.map((option) => option.id));
        const customSelected = budget.length > 0 && !fixedBudgetIds.has(budget);
        return {
          options: [
            ...BUDGET_OPTIONS.map((option) => ({
              id: option.id,
              title: intl.formatMessage(option.label),
              detail: intl.formatMessage(option.detail),
              meta: `USD ${option.amount}`,
              image: option.image,
              Icon: option.Icon,
            })),
            {
              id: 'custom-budget',
              title: intl.formatMessage(messages.budgetCustom),
              custom: true,
              accentCircle: true,
              image: BUDGET_IMAGES['2500'],
              customValue: validBudget ? Math.min(budgetNumber, CUSTOM_BUDGET_MAX) : 0,
              customMin: 0,
              customMax: CUSTOM_BUDGET_MAX,
              customUnit: intl.formatMessage(messages.budgetCustomUnit),
              customDecreaseLabel: intl.formatMessage(messages.budgetCustomFewer),
              customIncreaseLabel: intl.formatMessage(messages.budgetCustomMore),
              onCustomStep: (delta: number) => {
                touched.current = true;
                advanceOnArrival.current = false;
                const current = validBudget ? budgetNumber : 0;
                const next = Math.min(
                  Math.max(current + delta * CUSTOM_BUDGET_STEP, 0),
                  CUSTOM_BUDGET_MAX,
                );
                choose('/budget', String(next));
              },
              Icon: Wallet,
            },
          ],
          selected: customSelected ? 'custom-budget' : budget,
          pick: (id) => {
            if (id === 'custom-budget') {
              pickOnBoard('/budget', validBudget ? budget : '0', validBudget);
              return;
            }
            pickOnBoard('/budget', id, validBudget);
          },
        };
      }
      case 'identity':
        return { options: [], selected: '', pick: () => undefined };
    }
  };

  /** The stepper writes a clamped number, so the document can never hold nonsense. */
  const stepChildren = (delta: number): void => {
    choose('/children', String(Math.min(Math.max(childCount + delta, 0), CHILDREN_MAX)));
  };

  const selectedTraveller = GENDER_OPTIONS.find((option) => option.id === gender);

  /*
   * The last stop is a form, not a walk. It stands exactly as tall as the board it
   * replaces (590px, the plate's own height), so the screen keeps its shape as the
   * traveller crosses from the map to the card. The left third is a quiet visual
   * column for the one supplied traveller illustration that matches the selected
   * state; the whole document lives in the two-thirds panel, between the closing
   * line and the button that ends the journey.
   */
  const renderIdentityRound = (): JSX.Element => (
    <div className="grid gap-4 lg:h-[590px] lg:grid-cols-[1fr_2fr]">
      <div
        className="relative isolate hidden overflow-hidden rounded-[15px] border border-border bg-muted/30 lg:flex lg:items-end lg:justify-center lg:px-2 lg:pt-5"
        aria-hidden="true"
      >
        <div className="pointer-events-none absolute bottom-[-9%] left-1/2 z-0 h-[68%] w-[84%] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />
        {selectedTraveller ? (
          <img
            key={selectedTraveller.id}
            src={selectedTraveller.image}
            alt=""
            className={`relative z-10 ${selectedTraveller.id === 'male' ? TRAVELLER_FIGURE_MALE_CLASS : TRAVELLER_FIGURE_CLASS} animate-fadeUp motion-reduce:animate-none`}
          />
        ) : null}
      </div>

      <section className="flex flex-col overflow-hidden rounded-[15px] border border-border bg-muted/30 p-5 sm:p-6">
        <div>
          <div className="flex items-center gap-2 text-primary">
            <Sparkle size={18} weight="duotone" aria-hidden="true" />
            <span className="font-consolas text-[11px] font-semibold uppercase tracking-caps">
              {intl.formatMessage(messages.finishTitle)}
            </span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {intl.formatMessage(messages.finishHint)}
          </p>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-foreground">
              {intl.formatMessage(messages.firstName)}
            </span>
            <input
              ref={firstNameRef}
              type="text"
              value={firstName}
              onChange={handleText('/firstName')}
              onBlur={() => api?.commitValue()}
              placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
              autoComplete="given-name"
              className={PANEL_FIELD_CLASS}
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-foreground">
              {intl.formatMessage(messages.surname)}
            </span>
            <input
              ref={surnameRef}
              type="text"
              value={surname}
              onChange={handleText('/surname')}
              onBlur={() => api?.commitValue()}
              placeholder={intl.formatMessage(messages.surnamePlaceholder)}
              autoComplete="family-name"
              className={PANEL_FIELD_CLASS}
            />
          </label>

          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <IdentificationCard size={15} weight="duotone" aria-hidden="true" />
              {intl.formatMessage(messages.passportNumber)}
            </span>
            <input
              ref={passportNumberRef}
              type="text"
              inputMode="numeric"
              value={passportNumber}
              onChange={handleText('/passportNumber')}
              onBlur={() => api?.commitValue()}
              placeholder={intl.formatMessage(messages.passportNumberPlaceholder)}
              autoComplete="off"
              spellCheck={false}
              aria-describedby="travel-passport-number-hint"
              aria-invalid={showErrors && passportNumber.trim().length === 0}
              className={PANEL_FIELD_CLASS}
            />
            <span
              id="travel-passport-number-hint"
              className="mt-1.5 block text-[11px] leading-snug text-muted-foreground-subtle"
            >
              {intl.formatMessage(messages.passportNumberHint)}
            </span>
          </label>

          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <CalendarBlank size={15} weight="duotone" aria-hidden="true" />
              {intl.formatMessage(messages.dateOfBirth)}
            </span>
            <input
              ref={dateOfBirthRef}
              type="date"
              value={dateOfBirth}
              onChange={handleText('/dateOfBirth')}
              onBlur={() => api?.commitValue()}
              autoComplete="off"
              aria-describedby="travel-date-of-birth-hint"
              aria-invalid={showErrors && dateOfBirth.trim().length === 0}
              className={PANEL_FIELD_CLASS}
            />
            <span
              id="travel-date-of-birth-hint"
              className="mt-1.5 block text-[11px] leading-snug text-muted-foreground-subtle"
            >
              {intl.formatMessage(messages.dateOfBirthHint)}
            </span>
          </label>

          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <MapPin size={15} weight="duotone" aria-hidden="true" />
              {intl.formatMessage(messages.origin)}
            </span>
            <input
              ref={originRef}
              type="text"
              value={origin}
              onChange={handleText('/origin')}
              onBlur={() => api?.commitValue()}
              placeholder={intl.formatMessage(messages.originPlaceholder)}
              autoComplete="address-level2"
              className={PANEL_FIELD_CLASS}
            />
          </label>

          {/* Children: a plain counter, because nobody types "2" into a text box happily */}
          <div className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <Baby size={15} weight="duotone" aria-hidden="true" />
              {intl.formatMessage(messages.children)}
            </span>
            <div className="flex items-center gap-1 rounded-input border border-border bg-muted/40 px-1.5 py-1.5">
              <button
                type="button"
                onClick={() => stepChildren(-1)}
                disabled={childCount === 0}
                aria-label={intl.formatMessage(messages.childrenFewer)}
                className={STEPPER_BUTTON_CLASS}
              >
                <Minus size={15} weight="bold" aria-hidden="true" />
              </button>
              <span
                aria-live="polite"
                className="flex-1 text-center text-base font-bold tabular-nums text-foreground"
              >
                {childCount}
              </span>
              <button
                type="button"
                onClick={() => stepChildren(1)}
                disabled={childCount === CHILDREN_MAX}
                aria-label={intl.formatMessage(messages.childrenMore)}
                className={STEPPER_BUTTON_CLASS}
              >
                <Plus size={15} weight="bold" aria-hidden="true" />
              </button>
            </div>
            {childCount > 0 ? (
              <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground-subtle">
                {intl.formatMessage(messages.childrenSome)}
              </p>
            ) : null}
          </div>

          {/* The supplied figures live in the left column; these remain lightweight text controls. */}
          <div className="sm:col-span-2">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
              <UserCircle size={15} weight="duotone" aria-hidden="true" />
              {intl.formatMessage(messages.gender)}
            </span>
            <div
              className="flex gap-3"
              role="group"
              aria-label={intl.formatMessage(messages.gender)}
            >
              {GENDER_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  data-selected={gender === option.id}
                  aria-pressed={gender === option.id}
                  onClick={() => choose('/gender', option.id)}
                  className={GENDER_OPTION_CLASS}
                >
                  {intl.formatMessage(option.label)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* The final action sits cleanly at the bottom-right of the form. */}
        <div className="mt-auto flex flex-wrap items-center justify-end gap-4 pt-6">
          <button
            type="button"
            onClick={handleNext}
            disabled={completedRounds !== ROUND_DEFINITIONS.length}
            aria-disabled={completedRounds !== ROUND_DEFINITIONS.length}
            className="flex items-center justify-center gap-2 rounded-input bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition duration-150 [touch-action:manipulation] hover:brightness-110 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary disabled:cursor-not-allowed disabled:border disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:hover:brightness-100"
          >
            {intl.formatMessage(messages.showPlaces)}
            <ArrowRight size={17} weight="bold" aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );

  const stage = boardStage();

  const renderPlayArea = (): JSX.Element =>
    currentRound.id === 'identity' ? (
      renderIdentityRound()
    ) : (
      <QuestBoard
        stageKey={currentRound.id}
        options={stage.options}
        selectedId={stage.selected}
        onPick={stage.pick}
        onArrive={handleArrival}
        label={intl.formatMessage(messages.boardAria, {
          label: intl.formatMessage(currentRound.prompt),
        })}
      />
    );

  const progressPercent = Math.round((completedRounds / ROUND_DEFINITIONS.length) * 100);

  const openLabel = intl.formatMessage(messages.stampLocked);
  const stopValue = (roundId: RoundId): string => {
    const pick = (
      options: { id: string; label: MessageDescriptor }[],
      value: string,
    ): string => {
      const match = options.find((option) => option.id === value);
      return match ? intl.formatMessage(match.label) : openLabel;
    };
    switch (roundId) {
      case 'style':
        return pick(STYLE_OPTIONS, tripStyle);
      case 'weather':
        return pick(WEATHER_OPTIONS, season);
      case 'visa':
        return pick(VISA_OPTIONS, visa);
      case 'duration':
        return validDays ? intl.formatMessage(messages.daysValue, { days: daysNumber }) : openLabel;
      case 'budget':
        return validBudget
          ? intl.formatNumber(budgetNumber, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            })
          : openLabel;
      case 'identity':
        return firstName.trim().length > 0 ? firstName.trim() : openLabel;
    }
  };

  const stampSlots: StampSlot[] = ROUND_DEFINITIONS.map((definition) => ({
    id: definition.id,
    label: intl.formatMessage(definition.label),
    value: stopValue(definition.id),
    done: roundReady(definition.id),
    Icon: definition.Icon,
  }));

  return (
    <div
      className="flex w-full flex-col gap-8"
      data-wayfarer-step="2"
      data-wayfarer-step-available-through={completedRounds === ROUND_DEFINITIONS.length ? 3 : 2}
    >
      <div>
        <h1 className="text-balance text-3xl font-bold leading-[1.08] tracking-tighter text-foreground sm:text-4xl">
          {title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          {subtitle || intl.formatMessage(messages.builderHint)}
        </p>
      </div>

      {/*
        No panel around any of this. The stamps and the board sit straight on the
        page, so the board's own drawn plate is the only frame in play — a card
        behind it only put a second, competing edge around the same content.
      */}
      <section
        className="relative flex flex-col gap-[17px]"
        aria-labelledby="travel-passport-round-heading"
      >
        {/*
          The stamps are the navigation. They sit above the board because they are
          how you move between stops — each one is a place you can walk back to,
          and together they read as the trip taking shape. The stop's own question
          is carried by the cards, so it stays here for screen readers only.
        */}
        <div className="relative flex flex-col gap-4">
          <h2 id="travel-passport-round-heading" className="sr-only">
            {intl.formatMessage(currentRound.prompt)}
          </h2>
          {/* Just the count, on its own at the right: the stamps below say what
              they are, so a label over them was only repeating itself. */}
          <div className="flex items-center justify-end gap-2">
            <span className="font-consolas text-[11px] font-semibold uppercase tracking-caps text-muted-foreground-subtle">
              {intl.formatMessage(messages.stopsCleared, {
                count: completedRounds,
                total: ROUND_DEFINITIONS.length,
              })}
            </span>
            <span className="font-consolas text-[11px] font-semibold tabular-nums text-primary">
              {progressPercent}%
            </span>
          </div>

          <StampRail slots={stampSlots} current={round} onSelect={goToRound} />
        </div>

        <div className="relative">{renderPlayArea()}</div>

        {showErrors ? (
          <p
            className="relative flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3.5 py-3 text-sm font-medium text-destructive"
            role="alert"
          >
            <ShieldCheck size={17} weight="duotone" className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              {intl.formatMessage(
                currentRound.id === 'style'
                  ? messages.missingStyle
                  : currentRound.id === 'weather'
                    ? messages.missingWeather
                    : currentRound.id === 'visa'
                      ? messages.missingVisa
                      : currentRound.id === 'duration'
                        ? messages.missingDuration
                        : currentRound.id === 'budget'
                          ? messages.missingBudget
                          : messages.missingIdentity,
              )}
            </span>
          </p>
        ) : null}

        {/*
          No stepper buttons: on the card stops the traveller's arrival advances
          the trip, and the stamps above are how you go back. The last stop is a
          form, so it carries the one button that ends the journey — inside the
          finish panel, at the end of the walker's own dashed trail.
        */}
      </section>
    </div>
  );
};
